import { useEffect, useMemo, useState } from "react";
import { useStoredState } from "@/hooks/useStoredState";
import { Link } from "react-router-dom";
import { Search, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useInfiniteFeed } from "@/api/useInfiniteFeed";
import { Badge } from "@/components/ui/Badge";
import { LoadMoreButton } from "@/components/ui/LoadMoreButton";
import { PageLoader } from "@/components/ui/Spinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { VisibleAmountSum } from "@/components/ui/VisibleAmountSum";
import { XlsxExportButton } from "@/components/ui/XlsxExportButton";
import { ordersApi } from "../api/orders";
import { ORDER_ACCESS_TYPE_LABELS, ORDER_STATUS_LABELS, ORDER_TYPE_LABELS, PRIORITY_LABELS, type Order, type OrderAccessType, type OrderStatus, type OrderType, type Priority } from "../types";
import { OrderTypeBadge } from "../components/OrderTypeBadge";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import { useNavBadgeCounts } from "@/hooks/useNavBadges";
import { orderTodos } from "../utils/orderTodos";

interface ProjectFilterOption {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
  client: string | null;
  client_detail: { id: string; name: string; short_name: string } | null;
}

interface ContractorFilterOption {
  id: string;
  name: string;
  short_name: string;
  is_active: boolean;
}

function useProjectFilterOptions() {
  return useQuery({
    queryKey: ["orders-filter-projects"],
    queryFn: () =>
      apiClient
        .get<{ data: ProjectFilterOption[] }>("/projects/")
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });
}

function useContractorFilterOptions() {
  return useQuery({
    queryKey: ["orders-filter-contractors"],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorFilterOption[] }>("/contractors/")
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });
}

const statusColors: Record<OrderStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"> = {
  sent: "blue", accepted: "green", rejected: "red", in_progress: "orange",
  in_progress_partial: "yellow", submitted_for_acceptance: "purple",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

type SortField =
  | "order_number"
  | "contractor__name"
  | "priority"
  | "status"
  | "deadline"
  | "approved_at"
  | "total_value";
type SortDir = "asc" | "desc";

function SortableTh({
  field, label, sortField, sortDir, onSort, align = "left", title,
}: {
  field: SortField;
  label: string;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
  align?: "left" | "right";
  title?: string;
}) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      title={title}
      className={`${align === "right" ? "text-right" : "text-left"} px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 whitespace-nowrap`}
    >
      {label}{" "}
      <span className={active ? "text-[var(--color-primary)]" : "opacity-30"}>
        {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

/**
 * Miejscowości, których dotyczy zamówienie. Pierwsze dwie wprost, reszta pod „+n",
 * żeby jedna szeroka pozycja nie rozpychała całej tabeli.
 */
function LocalityCell({ order }: { order: Order }) {
  const names = order.localities ?? [];
  if (names.length === 0) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  const shown = names.slice(0, 2).join(", ");
  const rest = names.length - 2;
  const source =
    order.localities_source === "project"
      ? "Z działek przedsięwzięcia (zamówienie nie ma własnych działek)"
      : "Z działek zamówienia";
  return (
    <span title={`${names.join(", ")}\n${source}`}>
      {shown}
      {rest > 0 && <span className="text-gray-400 dark:text-gray-500"> +{rest}</span>}
    </span>
  );
}

const SELECT_CLS =
  "text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

// One value per attention "dot" the /orders sidebar badge aggregates — all in the chip row.
// `stale_unconfirmed` = orders flagged „długo czekają na przyjęcie" (director-only chip;
// backend: apps.orders.acceptance_deadline).
type AttentionFilter =
  | "change_requests"
  | "comments"
  | "external_costs"
  | "stale_unconfirmed"
  | "awaiting_my_rating"
  | null;

function BadgeDot({ color }: { color: "amber" | "red" | "brand" }) {
  const cls =
    color === "amber" ? "bg-amber-400" : color === "red" ? "bg-red-500" : "bg-[var(--color-primary)]";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${cls}`} />;
}

/**
 * Clickable summary of the GK "attention" queues that feed the /orders sidebar badge —
 * mirrors the /tenders badge row: each chip says what it is and filters the feed to
 * exactly those orders. Shown only to GK (director/coordinator); counts come from the
 * shared nav-badge cache, so they always match the sidebar dots.
 */
function OrdersBadgeSummary({
  activeFilter,
  onFilter,
  isGK,
  isDirector,
}: {
  activeFilter: AttentionFilter;
  onFilter: (f: AttentionFilter) => void;
  isGK: boolean;
  isDirector: boolean;
}) {
  const badges = useNavBadgeCounts();

  if (!isGK) return null;

  const items: Array<{
    color: "amber" | "red" | "brand";
    count: number;
    label: string;
    filter: AttentionFilter;
    title?: string;
  }> = [
    {
      color: "amber",
      count: badges.orders_change_requests_pending,
      label: "wnioski o zmianę zamówienia",
      filter: "change_requests",
    },
    {
      color: "red",
      count: badges.orders_pending_comments,
      label: "nieodczytane komentarze",
      filter: "comments",
    },
    {
      color: "amber",
      count: badges.orders_external_costs_pending,
      label: "wnioski o koszty zewnętrzne",
      filter: "external_costs",
    },
    // Dyrekcja: „Wysłane” po terminie przyjęcia (flaga z nocnego zadania). Nic nie jest
    // anulowane samo — chip podsuwa, że leżą długo i trzeba zdecydować.
    ...(isDirector
      ? [
          {
            color: "red" as const,
            count: badges.orders_stale_unconfirmed,
            label: "leżą długo bez przyjęcia",
            filter: "stale_unconfirmed" as const,
            title:
              "Wysłane zamówienia, których podwykonawca nie przyjął w terminie (umowa ramowa, domyślnie 5 dni). " +
              "Nadal czekają na podwykonawcę — zadzwoń, przedłuż termin albo anuluj zamówienie.",
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-wrap gap-3 mb-5">
      {items.map((item) => {
        const isActive = activeFilter === item.filter;
        return (
          <button
            key={item.label}
            title={item.title}
            onClick={() => onFilter(isActive ? null : item.filter)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors cursor-pointer ${
              isActive
                ? "bg-gray-200 dark:bg-gray-600 border-gray-400 dark:border-gray-400"
                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            <BadgeDot color={item.color} />
            <span className="font-semibold text-gray-900 dark:text-gray-100">{item.count}</span>
            <span className="text-gray-500 dark:text-gray-400">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** „Długo czeka” przy statusie — zamówienie „Wysłane” po terminie przyjęcia (flaga backendu). */
function OverdueAcceptanceBadge({ order }: { order: Order }) {
  if (order.status !== "sent" || !order.acceptance_overdue_since) return null;
  return (
    <Badge
      color="red"
      title={`Termin przyjęcia minął${order.confirmation_deadline ? ` ${formatDate(order.confirmation_deadline)}` : ""} — podwykonawca nie przyjął zamówienia`}
    >
      Długo czeka
    </Badge>
  );
}

/** Rodzaj nadanego dostępu przy numerze — tylko gdy zamówienie widać Z GRANTU. */
function AccessBadge({ order }: { order: Order }) {
  if (!order.access_via_grant || !order.access_level || order.access_level === "full" || order.access_level === "contractor") return null;
  const label = ORDER_ACCESS_TYPE_LABELS[order.access_level];
  return (
    <Badge color={order.access_level === "manage" ? "blue" : order.access_level === "execute" ? "orange" : "gray"} title="Twój dostęp do tego zamówienia">
      {label}
    </Badge>
  );
}

export function OrdersListPage() {
  // Filtry i sortowanie przeżywają wejście w szczegóły zamówienia i powrót
  // (sessionStorage) — bez ponownego ustawiania wszystkiego po każdym „wstecz".
  const [sortField, setSortField] = useStoredState<SortField | null>("orders-list:sort-field", null);
  const [sortDir, setSortDir] = useStoredState<SortDir>("orders-list:sort-dir", "asc");
  const [filterStatus, setFilterStatus] = useStoredState<OrderStatus | "">("orders-list:status", "");
  const [filterPriority, setFilterPriority] = useStoredState<Priority | "">("orders-list:priority", "");
  const [filterType, setFilterType] = useStoredState<OrderType | "">("orders-list:order-type", "");
  const [filterClient, setFilterClient] = useStoredState<string>("orders-list:client", "");
  const [filterProject, setFilterProject] = useStoredState<string>("orders-list:project", "");
  const [filterContractor, setFilterContractor] = useStoredState<string>("orders-list:contractor", "");
  const [filterAttention, setFilterAttention] = useStoredState<AttentionFilter>("orders-list:attention", null);
  // „Typ dostępu" — dla osób, które zamówienia widzą z nadanego dostępu (pracownik,
  // koordynator spoza przedsięwzięcia): Podgląd / Zarządzanie / Realizacja.
  const [filterAccessType, setFilterAccessType] = useStoredState<OrderAccessType | "">("orders-list:access-type", "");
  // Szukajka leci do serwera (?q=), więc obejmuje CAŁY zbiór, a nie doczytaną porcję.
  // Wpisywanie jest debounce'owane, żeby każda literka nie była osobnym zapytaniem.
  const [searchInput, setSearchInput] = useStoredState("orders-list:search", "");
  // Start od przywróconej frazy — bez tego pierwszy fetch po powrocie leciałby bez ?q=.
  const [search, setSearch] = useState(() => searchInput.trim());
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  const ordering = sortField ? (sortDir === "desc" ? `-${sortField}` : sortField) : undefined;
  const params: Record<string, string> = {};
  if (ordering) params.ordering = ordering;
  if (filterStatus) params.status = filterStatus;
  if (filterPriority) params.priority = filterPriority;
  if (filterType) params.order_type = filterType;
  if (filterClient) params.client = filterClient;
  if (filterProject) params.project = filterProject;
  if (filterContractor) params.contractor = filterContractor;
  if (filterAttention) params.attention = filterAttention;
  if (filterAccessType) params.access_type = filterAccessType;
  if (search) params.q = search;

  const {
    items: orders,
    isLoading,
    isPlaceholderData,
    total,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchAll,
    isFetchingAll,
  } = useInfiniteFeed<Order>({ key: ["orders", "feed"], url: "/orders/feed/", filters: params });
  const { data: projectOptions } = useProjectFilterOptions();
  const { data: contractorOptions } = useContractorFilterOptions();
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isGK = user?.role === "director" || user?.role === "coordinator";
  // Zakończone zamówienia bez oceny podwykonawcy, za które odpowiada TA osoba — ten sam
  // licznik, co czarny badge w menu (wspólny cache nav-badge).
  const awaitingMyRating = useNavBadgeCounts().orders_awaiting_my_rating;
  const hasActiveFilters =
    !!filterStatus ||
    !!filterPriority ||
    !!filterType ||
    !!filterClient ||
    !!filterProject ||
    !!filterContractor ||
    !!filterAttention ||
    !!filterAccessType ||
    !!searchInput;

  // Toggling an attention chip. `stale_unconfirmed` forces status=SENT server-side, so a
  // stale-conflicting status dropdown is cleared to avoid a confusing empty result.
  function handleAttentionFilter(f: AttentionFilter) {
    setFilterAttention(f);
    if (f === "stale_unconfirmed") setFilterStatus("");
  }

  const clientFilterOptions = useMemo(() => {
    if (!projectOptions) return [];
    const seen = new Map<string, { id: string; name: string; short_name: string }>();
    for (const p of projectOptions) {
      if (p.client && p.client_detail && !seen.has(p.client)) {
        seen.set(p.client, p.client_detail);
      }
    }
    return [...seen.values()].sort((a, b) =>
      (a.short_name || a.name).localeCompare(b.short_name || b.name, "pl"),
    );
  }, [projectOptions]);

  const sortedProjects = useMemo(() => {
    if (!projectOptions) return [];
    const filtered = filterClient
      ? projectOptions.filter((p) => p.client === filterClient)
      : projectOptions;
    return [...filtered].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return `${a.short_name} ${a.name}`.localeCompare(`${b.short_name} ${b.name}`, "pl");
    });
  }, [projectOptions, filterClient]);

  const sortedContractors = useMemo(() => {
    if (!contractorOptions) return [];
    return [...contractorOptions].sort((a, b) =>
      (a.short_name || a.name).localeCompare(b.short_name || b.name, "pl"),
    );
  }, [contractorOptions]);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Zamówienia</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
        </div>
        {/* Arkusz ze WSZYSTKIMI zamówieniami spełniającymi bieżące filtry — serwer
            dostaje te same parametry co feed, więc nie tylko doczytana porcja. */}
        <XlsxExportButton
          label="Pobierz zamówienia (.xlsx)"
          title="Pobierz wszystkie odfiltrowane zamówienia (nie tylko doczytane) jako arkusz Excel"
          count={total ?? orders.length}
          disabled={isLoading}
          download={() => ordersApi.downloadListXlsx(params)}
        />
      </div>
      <OrdersBadgeSummary
        activeFilter={filterAttention}
        onFilter={handleAttentionFilter}
        isGK={isGK}
        isDirector={isDirector}
      />
      {awaitingMyRating > 0 && (
        <button
          type="button"
          onClick={() =>
            handleAttentionFilter(filterAttention === "awaiting_my_rating" ? null : "awaiting_my_rating")
          }
          className={`mb-5 flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
            filterAttention === "awaiting_my_rating"
              ? "border-gray-900 bg-gray-200 dark:border-gray-300 dark:bg-gray-700"
              : "animate-pulse border-gray-900 bg-gray-900 text-white hover:bg-gray-800 dark:border-gray-300 dark:bg-black"
          }`}
        >
          <Star
            size={18}
            className={`mt-0.5 shrink-0 ${
              filterAttention === "awaiting_my_rating"
                ? "text-gray-900 dark:text-gray-100"
                : "text-amber-300"
            }`}
          />
          <div className="text-sm">
            <p
              className={`font-semibold ${
                filterAttention === "awaiting_my_rating"
                  ? "text-gray-900 dark:text-gray-100"
                  : "text-white"
              }`}
            >
              Zamówienia czekające na Twoją ocenę podwykonawcy: {awaitingMyRating}
            </p>
            <p
              className={`mt-0.5 text-xs ${
                filterAttention === "awaiting_my_rating"
                  ? "text-gray-700 dark:text-gray-300"
                  : "text-gray-300"
              }`}
            >
              {filterAttention === "awaiting_my_rating"
                ? "Filtr aktywny — kliknij, aby pokazać wszystkie. Wejdź w zamówienie i wystaw ocenę (jakość, współpraca, terminowość)."
                : "Odebrane i nieocenione — oceny decydują, kogo dobieramy do kolejnych zleceń. Kliknij, aby odfiltrować."}
            </p>
          </div>
        </button>
      )}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Szukaj: nr, miejscowość, działka…"
            title="Szuka po całej bazie zamówień — numer, opis, podwykonawca, przedsięwzięcie, miejscowość i numer działki"
            className={`${SELECT_CLS} pl-7 w-64`}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as OrderStatus | "")}
          className={SELECT_CLS}
        >
          <option value="">Wszystkie statusy</option>
          {(Object.entries(ORDER_STATUS_LABELS) as [OrderStatus, string][]).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value as Priority | "")}
          className={SELECT_CLS}
        >
          <option value="">Wszystkie priorytety</option>
          {(Object.entries(PRIORITY_LABELS) as [Priority, string][]).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as OrderType | "")}
          className={SELECT_CLS}
          title="Rodzaj pracy: WPW, MDCP, opracowanie ZE…"
        >
          <option value="">Wszystkie typy</option>
          {(Object.entries(ORDER_TYPE_LABELS) as [OrderType, string][]).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <select
          value={filterClient}
          onChange={(e) => {
            setFilterClient(e.target.value);
            setFilterProject("");
          }}
          className={SELECT_CLS}
        >
          <option value="">Wszyscy klienci</option>
          {clientFilterOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.short_name || c.name}
            </option>
          ))}
        </select>
        <SearchableSelect
          placeholder="Wszystkie przedsięwzięcia"
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          options={sortedProjects.map((p) => ({
            value: p.id,
            label: `${p.short_name} — ${p.name}${p.is_active ? "" : " [nieaktywne]"}`,
          }))}
        />
        <select
          value={filterContractor}
          onChange={(e) => setFilterContractor(e.target.value)}
          className={SELECT_CLS}
        >
          <option value="">Wszyscy podwykonawcy</option>
          {sortedContractors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.short_name ? `${c.short_name} — ${c.name}` : c.name}
            </option>
          ))}
        </select>
        {user?.has_granted_order_access && (
          <select
            value={filterAccessType}
            onChange={(e) => setFilterAccessType(e.target.value as OrderAccessType | "")}
            className={SELECT_CLS}
            title="Zamówienia, do których masz nadany dostęp danego rodzaju"
          >
            <option value="">Każdy typ dostępu</option>
            {(Object.entries(ORDER_ACCESS_TYPE_LABELS) as [OrderAccessType, string][]).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            onClick={() => {
              setFilterStatus("");
              setFilterPriority("");
              setFilterType("");
              setFilterClient("");
              setFilterProject("");
              setFilterContractor("");
              setFilterAttention(null);
              setFilterAccessType("");
              setSearchInput("");
            }}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
          >
            Wyczyść filtry
          </button>
        )}
      </div>
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className={isPlaceholderData ? "opacity-60 pointer-events-none" : undefined}>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {orders.map((order) => {
              const todos = orderTodos(order, user);
              return (
                <div key={order.id} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link to={`/orders/${order.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                        {order.order_number}
                      </Link>
                      <OrderTypeBadge type={order.order_type} display={order.order_type_display} hideStandard />
                      <AccessBadge order={order} />
                      {todos.map((t) => (
                        <span key={t.key} className={t.className} title={t.title}>
                          {t.label}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{order.project_name}</p>
                    {(order.localities ?? []).length > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        Miejscowość: {order.localities.join(", ")}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{order.contractor_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Wartość: <VisibleAmountSum values={order.line_items.map((li) => li.total_value)} />
                    </p>
                    {order.deadline && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Termin: {formatDate(order.deadline)}</p>}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Dodane: {formatDate(order.approved_at)} · {order.approved_by_name}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge color={statusColors[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
                    <OverdueAcceptanceBadge order={order} />
                    <Badge color={order.priority === "high" ? "red" : order.priority === "low" ? "gray" : "blue"}>
                      {PRIORITY_LABELS[order.priority]}
                    </Badge>
                  </div>
                </div>
              );
            })}
            {orders.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">Brak zamówień</p>
            )}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800">
            <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1100px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Lp.</th>
                  <SortableTh field="order_number" label="Nr zamówienia" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Miejscowość</th>
                  <SortableTh field="contractor__name" label="Podwykonawca" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="priority" label="Priorytet" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh
                    field="total_value"
                    label="Wartość"
                    sortField={sortField}
                    sortDir={sortDir}
                    onSort={handleSort}
                    align="right"
                    title="Wartość netto pozycji — suma widocznych dla Ciebie kwot. Sortowanie po tej samej sumie; wiersze bez kwoty zawsze na końcu."
                  />
                  <SortableTh field="deadline" label="Termin" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="approved_at" label="Dodane przez" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((order, idx) => {
                  const todos = orderTodos(order, user);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-2 border-transparent hover:border-[var(--color-primary)] transition-colors">
                      <td className="px-4 py-3 text-gray-400 dark:text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/orders/${order.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                            {order.order_number}
                          </Link>
                          <OrderTypeBadge type={order.order_type} display={order.order_type_display} hideStandard />
                          <AccessBadge order={order} />
                          {todos.map((t) => (
                            <span key={t.key} className={t.className} title={t.title}>
                              {t.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{order.project_name}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[180px] truncate">
                        <LocalityCell order={order} />
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{order.contractor_name}</td>
                      <td className="px-4 py-3">
                        <Badge color={order.priority === "high" ? "red" : order.priority === "low" ? "gray" : "blue"}>
                          {PRIORITY_LABELS[order.priority]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge color={statusColors[order.status]}>{ORDER_STATUS_LABELS[order.status]}</Badge>
                          <OverdueAcceptanceBadge order={order} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        <VisibleAmountSum values={order.line_items.map((li) => li.total_value)} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(order.deadline)}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        <div>{order.approved_by_name}</div>
                        <div className="text-gray-400 dark:text-gray-500">{formatDate(order.approved_at)}</div>
                      </td>
                    </tr>
                  );
                })}
                {orders.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">Brak zamówień</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
          {/* One footer for both layouts — they are mutually exclusive by breakpoint. */}
          <LoadMoreButton
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onLoadAll={fetchAll}
            isFetchingAll={isFetchingAll}
            loaded={orders.length}
            total={total}
          />
        </div>
      )}
    </div>
  );
}
